import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_CORSO = {
  id: null,
  titolo: "",
  istruttori: [""],
  data: "",
  sede: "",
  durata: "",
  argomenti: [""],
  partecipanti: [],
  firmatario_attestato: "",
  ruolo_firmatario: "Il direttore didattico",
};

export default function PrivacyCorsiPage() {
  const [corsi, setCorsi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(null); // corso obj o null
  const [docentiPick, setDocentiPick] = useState([]);
  const [esterniPick, setEsterniPick] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQ, setPickerQ] = useState("");

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await api.get("/privacy/corsi");
      setCorsi(r.data?.items || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openNew = async () => {
    await loadLookups();
    setEditing({ ...EMPTY_CORSO });
  };

  const openEdit = async (corso) => {
    await loadLookups();
    setEditing({
      ...EMPTY_CORSO,
      ...corso,
      istruttori: corso.istruttori?.length ? corso.istruttori : [""],
      argomenti: corso.argomenti?.length ? corso.argomenti : [""],
      partecipanti: corso.partecipanti || [],
    });
  };

  const loadLookups = async () => {
    if (docentiPick.length || esterniPick.length) return;
    try {
      const r = await api.get("/privacy/corsi/_lookups/partecipanti");
      setDocentiPick(r.data?.docenti || []);
      setEsterniPick(r.data?.esterni || []);
    } catch { /* ignore */ }
  };

  const save = async () => {
    if (!editing?.titolo?.trim()) {
      alert("Il titolo del corso è obbligatorio");
      return;
    }
    try {
      const body = {
        titolo: editing.titolo,
        istruttori: (editing.istruttori || []).filter(Boolean),
        data: editing.data,
        sede: editing.sede,
        durata: editing.durata,
        argomenti: (editing.argomenti || []).filter(x => x && x.trim()),
        partecipanti: editing.partecipanti || [],
        firmatario_attestato: editing.firmatario_attestato,
        ruolo_firmatario: editing.ruolo_firmatario,
      };
      if (editing.id) {
        await api.put(`/privacy/corsi/${editing.id}`, body);
      } else {
        await api.post("/privacy/corsi", body);
      }
      setEditing(null);
      await load();
    } catch (e) {
      alert("Errore salvataggio: " + (e.response?.data?.detail || e.message));
    }
  };

  const removeCorso = async (corso) => {
    if (!window.confirm(`Eliminare definitivamente il corso "${corso.titolo}"?`)) return;
    try {
      await api.delete(`/privacy/corsi/${corso.id}`);
      await load();
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    }
  };

  const openVerbalePdf = (corso) => {
    window.open(`${REACT_APP_BACKEND_URL}/api/privacy/corsi/${corso.id}/verbale-pdf`, "_blank");
  };
  const openAttestatiTuttiPdf = (corso) => {
    window.open(`${REACT_APP_BACKEND_URL}/api/privacy/corsi/${corso.id}/attestati-pdf-tutti`, "_blank");
    setTimeout(load, 1500);
  };

  return (
    <div data-testid="privacy-corsi-page" style={{ padding: "12px 20px" }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
        <h1 style={{ margin: 0, fontSize: 18, color: "#0d47a1" }}>🎓 Archivio Corsi Privacy</h1>
        <button data-testid="corsi-new-btn" onClick={openNew}
          style={{ marginLeft: "auto", padding: "7px 18px", background: "#0d47a1", color: "white", border: "none", borderRadius: 3, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          ➕ Nuovo Corso
        </button>
      </div>

      {err && <div style={{ padding: 8, background: "#ffebee", color: "#c62828", border: "1px solid #ef9a9a", marginBottom: 8, fontSize: 12 }}>{err}</div>}
      {loading && <div style={{ padding: 12, fontSize: 12, color: "#666" }}>Caricamento…</div>}

      <div style={{ overflowX: "auto", border: "1px solid #ddd" }}>
        <table className="sw-table" style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
          <thead className="thead-blue">
            <tr>
              <th style={th}>DATA</th>
              <th style={th}>TITOLO</th>
              <th style={th}>ISTRUTTORE</th>
              <th style={th}>SEDE</th>
              <th style={th}>DURATA</th>
              <th style={{ ...th, textAlign: "center" }}>PARTEC.</th>
              <th style={{ ...th, textAlign: "center", width: 440 }}>AZIONI</th>
            </tr>
          </thead>
          <tbody>
            {!loading && corsi.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 20, textAlign: "center", color: "#999" }}>Nessun corso registrato</td></tr>
            )}
            {corsi.map(c => (
              <CorsoRow key={c.id} corso={c} onEdit={openEdit} onDelete={removeCorso} onVerbale={openVerbalePdf} onAttestatiTutti={openAttestatiTuttiPdf} />
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <CorsoEditModal
          corso={editing}
          setCorso={setEditing}
          onClose={() => setEditing(null)}
          onSave={save}
          docentiPick={docentiPick}
          esterniPick={esterniPick}
          pickerOpen={pickerOpen}
          setPickerOpen={setPickerOpen}
          pickerQ={pickerQ}
          setPickerQ={setPickerQ}
        />
      )}
    </div>
  );
}

function CorsoRow({ corso, onEdit, onDelete, onVerbale, onAttestatiTutti }) {
  const partecipanti = corso.partecipanti || [];
  return (
    <tr style={{ borderBottom: "1px solid #eee" }}>
      <td style={td}>{fmtData(corso.data)}</td>
      <td style={{ ...td, fontWeight: 700 }}>{corso.titolo}</td>
      <td style={td}>{(corso.istruttori || []).join(", ") || "—"}</td>
      <td style={td}>{corso.sede || "—"}</td>
      <td style={td}>{corso.durata || "—"}</td>
      <td style={{ ...td, textAlign: "center" }}>{partecipanti.length}</td>
      <td style={{ ...td, textAlign: "center", whiteSpace: "nowrap", position: "relative" }}>
        <button data-testid={`corso-edit-${corso.id}`} onClick={() => onEdit(corso)}
          style={btnBlue}>✏ Modifica</button>
        <button data-testid={`corso-verbale-${corso.id}`} onClick={() => onVerbale(corso)}
          style={btnGreen}>📄 Verbale</button>
        <button data-testid={`corso-att-tutti-${corso.id}`}
          onClick={() => onAttestatiTutti(corso)}
          disabled={partecipanti.length === 0}
          title={partecipanti.length === 0 ? "Nessun partecipante" : `Stampa tutti gli attestati (${partecipanti.length}) in un unico PDF`}
          style={{ ...btnPurple, background: partecipanti.length === 0 ? "#bdbdbd" : "#1b5e20", border: partecipanti.length === 0 ? "none" : "1px solid #0a3814" }}>
          📜 Attestati ({partecipanti.length})
        </button>
        <button data-testid={`corso-del-${corso.id}`} onClick={() => onDelete(corso)}
          style={btnRed}>🗑</button>
      </td>
    </tr>
  );
}

function CorsoEditModal({ corso, setCorso, onClose, onSave, docentiPick, esterniPick, pickerOpen, setPickerOpen, pickerQ, setPickerQ }) {
  const setField = (k, v) => setCorso(prev => ({ ...prev, [k]: v }));
  const setIstruttoreAt = (i, v) => setCorso(prev => {
    const arr = [...(prev.istruttori || [])];
    arr[i] = v;
    return { ...prev, istruttori: arr };
  });
  const addIstruttore = () => setCorso(prev => ({ ...prev, istruttori: [...(prev.istruttori || []), ""] }));
  const removeIstruttore = (i) => setCorso(prev => {
    const arr = (prev.istruttori || []).filter((_, idx) => idx !== i);
    return { ...prev, istruttori: arr.length ? arr : [""] };
  });
  const setArgomentoAt = (i, v) => setCorso(prev => {
    const arr = [...(prev.argomenti || [])];
    arr[i] = v;
    return { ...prev, argomenti: arr };
  });
  const addArgomento = () => setCorso(prev => ({ ...prev, argomenti: [...(prev.argomenti || []), ""] }));
  const removeArgomento = (i) => setCorso(prev => {
    const arr = (prev.argomenti || []).filter((_, idx) => idx !== i);
    return { ...prev, argomenti: arr.length ? arr : [""] };
  });
  const addPartecipante = (p) => setCorso(prev => ({
    ...prev,
    partecipanti: [...(prev.partecipanti || []), { ...p }],
  }));
  const addPartecipanteManuale = () => addPartecipante({ source: "manuale", ref_codice: null, cognome: "", nome: "", mansione: "" });
  const updatePartecipante = (i, field, val) => setCorso(prev => {
    const arr = [...(prev.partecipanti || [])];
    arr[i] = { ...arr[i], [field]: val };
    return { ...prev, partecipanti: arr };
  });
  const removePartecipante = (i) => setCorso(prev => ({
    ...prev,
    partecipanti: (prev.partecipanti || []).filter((_, idx) => idx !== i),
  }));

  const filteredPick = useMemo(() => {
    const qn = (pickerQ || "").toLowerCase().trim();
    const all = [
      ...docentiPick.map(d => ({ ...d, label: `${d.cognome} ${d.nome}` })),
      ...esterniPick.map(d => ({ ...d, label: `${d.cognome} ${d.nome}`.trim() })),
    ];
    if (!qn) return all.slice(0, 200);
    return all.filter(d => `${d.cognome} ${d.nome} ${d.mansione || ""}`.toLowerCase().includes(qn)).slice(0, 200);
  }, [pickerQ, docentiPick, esterniPick]);

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "white", borderRadius: 6, padding: 20, width: "100%", maxWidth: 1100,
        maxHeight: "92vh", overflowY: "auto", boxShadow: "0 12px 32px rgba(0,0,0,0.3)",
      }}>
        <h2 style={{ margin: "0 0 14px 0", color: "#0d47a1", fontSize: 16 }}>
          {corso.id ? "✏ Modifica Corso" : "➕ Nuovo Corso"}
        </h2>

        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 10, marginBottom: 12 }}>
          <Field label="TITOLO DEL CORSO *">
            <input data-testid="corso-titolo" value={corso.titolo || ""} onChange={e => setField("titolo", e.target.value)} style={inputStyle} />
          </Field>
          <Field label="DATA">
            <input data-testid="corso-data" type="date" value={corso.data || ""} onChange={e => setField("data", e.target.value)} style={inputStyle} />
          </Field>
          <Field label="SEDE">
            <input data-testid="corso-sede" value={corso.sede || ""} onChange={e => setField("sede", e.target.value)} style={inputStyle} />
          </Field>
          <Field label="DURATA">
            <input data-testid="corso-durata" value={corso.durata || ""} onChange={e => setField("durata", e.target.value)} placeholder="es. 4 ore" style={inputStyle} />
          </Field>
        </div>

        {/* Istruttori */}
        <Section title="ISTRUTTORI">
          {(corso.istruttori || [""]).map((ist, i) => (
            <div key={i} style={{ display: "flex", gap: 6, marginBottom: 4 }}>
              <input data-testid={`corso-istruttore-${i}`} value={ist} onChange={e => setIstruttoreAt(i, e.target.value)}
                placeholder={`Istruttore ${i + 1}`} style={{ ...inputStyle, flex: 1 }} />
              <button onClick={() => removeIstruttore(i)} style={btnRedSmall}>✕</button>
            </div>
          ))}
          <button data-testid="corso-add-istruttore" onClick={addIstruttore} style={btnAdd}>+ Aggiungi istruttore</button>
        </Section>

        {/* Argomenti */}
        <Section title="ARGOMENTI TRATTATI">
          {(corso.argomenti || [""]).map((arg, i) => (
            <div key={i} style={{ display: "flex", gap: 6, marginBottom: 4, alignItems: "flex-start" }}>
              <span style={{ paddingTop: 8, color: "#555", fontWeight: 700, minWidth: 26, fontSize: 11 }}>{(i + 1).toString().padStart(2, "0")}</span>
              <textarea data-testid={`corso-argomento-${i}`} value={arg} onChange={e => setArgomentoAt(i, e.target.value)}
                rows={2} placeholder={`Argomento ${i + 1}`} style={{ ...inputStyle, flex: 1, resize: "vertical" }} />
              <button onClick={() => removeArgomento(i)} style={btnRedSmall}>✕</button>
            </div>
          ))}
          <button data-testid="corso-add-argomento" onClick={addArgomento} style={btnAdd}>+ Aggiungi argomento</button>
        </Section>

        {/* Partecipanti */}
        <Section title={`PARTECIPANTI (${(corso.partecipanti || []).length})`}>
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <button data-testid="corso-add-partecipante-archivio" onClick={() => setPickerOpen(true)} style={btnBlueAdd}>
              📚 Aggiungi da Archivio (Docenti / Esterni)
            </button>
            <button data-testid="corso-add-partecipante-manuale" onClick={addPartecipanteManuale} style={btnAdd}>
              + Riga libera
            </button>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead className="thead-blue">
              <tr>
                <th style={{ ...th, width: 40, textAlign: "center" }}>#</th>
                <th style={th}>COGNOME</th>
                <th style={th}>NOME</th>
                <th style={th}>MANSIONE</th>
                <th style={{ ...th, width: 110 }}>FONTE</th>
                <th style={{ ...th, width: 130, textAlign: "center" }}>PROTOCOLLO</th>
                <th style={{ ...th, width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {(corso.partecipanti || []).length === 0 && (
                <tr><td colSpan={7} style={{ padding: 14, textAlign: "center", color: "#999" }}>Nessun partecipante</td></tr>
              )}
              {(corso.partecipanti || []).map((p, i) => (
                <tr key={i}>
                  <td style={{ ...td, textAlign: "center", color: "#888" }}>{(i + 1).toString().padStart(2, "0")}</td>
                  <td style={td}><input data-testid={`p-cog-${i}`} value={p.cognome || ""} onChange={e => updatePartecipante(i, "cognome", e.target.value)} style={inputStyle} /></td>
                  <td style={td}><input data-testid={`p-nom-${i}`} value={p.nome || ""} onChange={e => updatePartecipante(i, "nome", e.target.value)} style={inputStyle} /></td>
                  <td style={td}><input data-testid={`p-man-${i}`} value={p.mansione || ""} onChange={e => updatePartecipante(i, "mansione", e.target.value)} style={inputStyle} /></td>
                  <td style={td}>
                    <span style={{
                      padding: "2px 8px", borderRadius: 3, fontSize: 10, fontWeight: 700,
                      background: p.source === "docente" ? "#e3f2fd" : p.source === "esterno" ? "#fff3e0" : "#eceff1",
                      color: p.source === "docente" ? "#0d47a1" : p.source === "esterno" ? "#e65100" : "#546e7a",
                    }}>
                      {p.source === "docente" ? "DOCENTE" : p.source === "esterno" ? "ESTERNO" : "MANUALE"}
                    </span>
                  </td>
                  <td style={{ ...td, textAlign: "center", fontFamily: "Consolas", fontSize: 10, color: p.attestato_protocollo ? "#1b5e20" : "#bbb" }}>
                    {p.attestato_protocollo || "—"}
                  </td>
                  <td style={{ ...td, textAlign: "center" }}>
                    <button data-testid={`p-del-${i}`} onClick={() => removePartecipante(i)} style={btnRedSmall}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>

        {/* Firma attestato */}
        <Section title="FIRMATARIO ATTESTATO DI PARTECIPAZIONE">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Field label="Ruolo">
              <input data-testid="corso-ruolo-firmatario" value={corso.ruolo_firmatario || ""} onChange={e => setField("ruolo_firmatario", e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Nome e Cognome">
              <input data-testid="corso-firmatario" value={corso.firmatario_attestato || ""} onChange={e => setField("firmatario_attestato", e.target.value)} style={inputStyle} />
            </Field>
          </div>
        </Section>

        <div style={{ marginTop: 16, display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button data-testid="corso-cancel" onClick={onClose} style={btnGrey}>Annulla</button>
          <button data-testid="corso-save" onClick={onSave} style={btnBlueLg}>💾 Salva</button>
        </div>

        {/* Picker partecipanti */}
        {pickerOpen && (
          <div onClick={() => setPickerOpen(false)} style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1100,
            display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
          }}>
            <div onClick={e => e.stopPropagation()} style={{
              background: "white", borderRadius: 6, padding: 16, width: "100%", maxWidth: 600,
              maxHeight: "80vh", overflowY: "auto",
            }}>
              <h3 style={{ margin: "0 0 10px 0", color: "#0d47a1", fontSize: 14 }}>📚 Scegli partecipante</h3>
              <input data-testid="picker-search" autoFocus value={pickerQ} onChange={e => setPickerQ(e.target.value)}
                placeholder="Cerca cognome, nome, mansione…" style={{ ...inputStyle, width: "100%", marginBottom: 10 }} />
              <div style={{ maxHeight: 380, overflowY: "auto", border: "1px solid #ddd" }}>
                {filteredPick.length === 0 && <div style={{ padding: 20, textAlign: "center", color: "#999", fontSize: 12 }}>Nessun risultato</div>}
                {filteredPick.map((d, i) => (
                  <button key={`${d.source}-${d.ref_codice}-${i}`} data-testid={`picker-pick-${i}`}
                    onClick={() => { addPartecipante(d); setPickerOpen(false); setPickerQ(""); }}
                    style={{
                      display: "block", width: "100%", textAlign: "left",
                      padding: "6px 10px", border: "none", background: "white", cursor: "pointer",
                      fontSize: 12, borderBottom: "1px solid #f0f0f0",
                    }}>
                    <b>{d.cognome} {d.nome}</b>
                    <span style={{
                      marginLeft: 8, padding: "1px 6px", fontSize: 9, fontWeight: 700, borderRadius: 2,
                      background: d.source === "docente" ? "#e3f2fd" : "#fff3e0",
                      color: d.source === "docente" ? "#0d47a1" : "#e65100",
                    }}>
                      {d.source === "docente" ? "DOCENTE" : "ESTERNO"}
                    </span>
                    {d.mansione && <span style={{ color: "#888", marginLeft: 8 }}>· {d.mansione}</span>}
                  </button>
                ))}
              </div>
              <div style={{ marginTop: 10, textAlign: "right" }}>
                <button onClick={() => setPickerOpen(false)} style={btnGrey}>Chiudi</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginTop: 14, padding: 12, background: "#fafafa", border: "1px solid #e0e0e0" }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: "#0d47a1", marginBottom: 8 }}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#555", marginBottom: 3, letterSpacing: 0.5 }}>{label}</div>
      {children}
    </div>
  );
}

function fmtData(s) {
  if (!s) return "—";
  try {
    const [y, m, d] = s.split("-");
    if (y && m && d) return `${d}/${m}/${y}`;
  } catch { /* noop */ }
  return s;
}

const th = { padding: "4px 6px", textAlign: "left", fontSize: 11, fontWeight: 700, letterSpacing: 0.5 };
const td = { padding: "5px 8px", verticalAlign: "middle", textAlign: "left", borderBottom: "1px solid #eee" };
const inputStyle = { padding: "5px 8px", border: "1px solid #ccc", fontSize: 12, width: "100%", boxSizing: "border-box" };
const btnBlue = { padding: "4px 10px", marginRight: 4, background: "#1565c0", color: "white", border: "none", borderRadius: 3, fontSize: 11, fontWeight: 700, cursor: "pointer" };
const btnGreen = { padding: "4px 10px", marginRight: 4, background: "#2e7d32", color: "white", border: "none", borderRadius: 3, fontSize: 11, fontWeight: 700, cursor: "pointer" };
const btnPurple = { padding: "4px 10px", marginRight: 4, background: "#6a1b9a", color: "white", border: "none", borderRadius: 3, fontSize: 11, fontWeight: 700, cursor: "pointer" };
const btnRed = { padding: "4px 8px", background: "#c62828", color: "white", border: "none", borderRadius: 3, fontSize: 11, fontWeight: 700, cursor: "pointer" };
const btnRedSmall = { padding: "4px 8px", background: "#c62828", color: "white", border: "none", borderRadius: 3, fontSize: 10, cursor: "pointer" };
const btnAdd = { padding: "5px 12px", background: "#eceff1", color: "#333", border: "1px solid #b0bec5", borderRadius: 3, fontSize: 11, fontWeight: 600, cursor: "pointer" };
const btnBlueAdd = { padding: "5px 12px", background: "#0d47a1", color: "white", border: "none", borderRadius: 3, fontSize: 11, fontWeight: 700, cursor: "pointer" };
const btnGrey = { padding: "7px 14px", background: "#eee", color: "#333", border: "1px solid #ccc", borderRadius: 3, fontSize: 12, cursor: "pointer" };
const btnBlueLg = { padding: "7px 20px", background: "#0d47a1", color: "white", border: "none", borderRadius: 3, fontSize: 12, fontWeight: 700, cursor: "pointer" };
